import React, { useMemo, useState } from "react";
import NavigationTabs from "../../../globalComponents/ui/NavigationTabs";
import EmailNotificationsAndDueDate from "../EmailNotificationsAndDueDate";
import QueryBuilder from "../../QueryBuilder";
import Reminders from "../../Reminders";
import EmailHistory from "../../EmailHistory";
import EmailTemplates from "../../EmailTemplates";
import AlertRulesList from "../../IotMeters/IotMeters/AlertRules/AlertRulesList";
import IotMeterAlerts from "../../IotMeters/IotMeters/IotMeterAlerts/IotMeterAlerts";
import ReportRulesList from "../../IotMeters/IotMeters/ReportRules/ReportRulesList";
import { useMeters } from "../../IotMeters/IotMeters/hooks/useMeters";
import { useFinancialYears, useSources } from "../../../hooks/useApiData";
import { usePermission } from "../../../hooks/usePermission";
import "./EmailNotifications.css";

/**
 * Email Notifications settings tab — one home for "who gets emailed about what,
 * and when": the Query Builder + reminder rules built on it, email templates and
 * history, and the IoT meter alert/report screens (moved here from the IoT Meters
 * sidebar) since they all deliver through the same centralized email pipeline.
 * Each section owns its own data — this component only switches between them.
 * @returns {JSX.Element}
 */
export default function EmailNotifications() {
  const [section, setSection] = useState("queryBuilder");

  const [canViewAlertRules] = usePermission(
    "iot-meters.view-alert-rules",
    "IOT_METERS",
  );
  const [canViewAlerts] = usePermission("iot-meters.view-alerts", "IOT_METERS");
  const [canViewReportRules] = usePermission(
    "iot-meters.view-report-rules",
    "IOT_METERS",
  );
  const showIot = canViewAlertRules || canViewAlerts || canViewReportRules;

  /** Meters + sources + financial years the IoT screens render from. */
  const { data: meters = [] } = useMeters();
  const sources = useSources();
  const financialYears = useFinancialYears();
  const sourceOptions = useMemo(
    () =>
      sources.map((s) => ({
        value: s.id,
        label: s.unitCode || s.location?.area || "Unknown",
        subLocations: (s.subLocation || []).map((sl) => ({
          value: sl.id,
          label: sl.subLocation,
        })),
      })),
    [sources],
  );
  const financialYearOptions = useMemo(
    () =>
      financialYears.map((fy) => ({
        value: fy.financial_year_value,
        label: fy.financial_year_value,
      })),
    [financialYears],
  );
  const currentFinancialYear = useMemo(
    () =>
      [...financialYears].sort(
        (a, b) => b.financial_year_value - a.financial_year_value,
      )?.[0]?.financial_year_value,
    [financialYears],
  );

  /** Sections in order; the IoT ones appear only with the matching permission. */
  const sections = useMemo(() => {
    const base = [
      { key: "queryBuilder", label: "Query builder", icon: "sliders-h" },
      { key: "reminders", label: "Reminder rules", icon: "clock" },
      { key: "templates", label: "Email templates", icon: "envelope-open-text" },
      { key: "history", label: "Email history", icon: "history" },
    ];
    if (canViewAlertRules)
      base.push({
        key: "alertRules",
        label: "IOT alert rules",
        icon: "exclamation-triangle",
      });
    if (canViewAlerts)
      base.push({ key: "alerts", label: "IOT alerts", icon: "bell" });
    if (canViewReportRules)
      base.push({
        key: "reportRules",
        label: "IOT report rules",
        icon: "chart-bar",
      });
    base.push({ key: "notifications", label: "Notifications", icon: "bell" });
    return base;
  }, [canViewAlertRules, canViewAlerts, canViewReportRules]);

  /** Jumps to the reminder rules section from the Query Builder. */
  const openReminders = () => setSection("reminders");

  /** Jumps back to the Query Builder from the reminder rules. */
  const openQueryBuilder = () => setSection("queryBuilder");

  return (
    <div className="ent-root">
      <NavigationTabs tabs={sections} activeKey={section} onChange={setSection} />

      {section === "notifications" && <EmailNotificationsAndDueDate />}
      {section === "queryBuilder" && (
        <QueryBuilder onOpenReminders={openReminders} />
      )}
      {section === "reminders" && (
        <Reminders onOpenQueryBuilder={openQueryBuilder} />
      )}
      {section === "templates" && <EmailTemplates />}
      {section === "history" && <EmailHistory />}

      {showIot && (
        <div className="ent-iot-section">
          {section === "alertRules" && (
            <AlertRulesList
              meters={meters}
              sources={sourceOptions}
              currentFinancialYear={currentFinancialYear}
            />
          )}
          {section === "alerts" && (
            <IotMeterAlerts
              meters={meters}
              sources={sourceOptions}
              currentFinancialYear={currentFinancialYear}
              financialYearOptions={financialYearOptions}
            />
          )}
          {section === "reportRules" && (
            <ReportRulesList
              meters={meters}
              sources={sourceOptions}
              currentFinancialYear={currentFinancialYear}
              financialYearOptions={financialYearOptions}
            />
          )}
        </div>
      )}
    </div>
  );
}
